/**
 * AuthProvider — subscribes to Supabase auth state changes and exposes
 * the current session + user via React context. Mounted near the root.
 *
 * Consumers read this through `useAuth()` (see ../hooks/useAuth.ts).
 */
import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, SUPABASE_CONFIGURED } from '@/lib/supabase';

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True while we wait for the first auth-state event after page load. */
  initializing: boolean;
  /** True when both Supabase env vars are present. */
  backendConfigured: boolean;
  signOut: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (!SUPABASE_CONFIGURED) {
      setInitializing(false);
      return;
    }

    let mounted = true;

    // Prime with whatever session is already in storage (PKCE-persisted).
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setInitializing(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setInitializing(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      initializing,
      backendConfigured: SUPABASE_CONFIGURED,
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [session, initializing],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
